<template>
  <main>
    <b-container v-if="apiReady && axisReady && analysis" fluid>
      <div class="page-breadcrumb">
        <router-link
          :to="{
            name: 'ExploreAnalysisView',
            params: { id: this.$route.params.id },
          }"
        >
          <i class="fa fa-angle-left" /> Back to analysis
        </router-link>
      </div>

      <div class="section-header">
        <h1 class="heading">Edit analysis</h1>

        <div class="actions"></div>
      </div>
      
      <ExploreAnalysisForm
        :allow-submit="allowSubmit"
        :axis="axis"
        :form="form"
        :year-limit="yearLimit"
        :year-range="yearRange"
        @callback="submit"
      >
      </ExploreAnalysisForm>
    </b-container>
  </main>
</template>

<script>
import _ from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import InitApi from "../Shared/InitApi";
import AnalysisHelpers from "./AnalysisHelpers";
import ExploreAnalysisForm from "./ExploreAnalysisForm";

export default {
  name: "ExploreAnalysisEdit",
  components: {
    ExploreAnalysisForm,
  },
  extends: InitApi,
  data() {
    return {
      analysis: null,
      axisReady: false,
      allowSubmit: true,
      yearRange: null,
      yearLimit: [],
      form: {
        type: null,
        name: null,
        description: null,
        recentyear: null,
        startyear: null,
        stopyear: null,
        includeSubset: false,
        addClustering: true,
      },
      axis: {
        x: {},
        y: {},
        z: {},
      },
    };
  },
  computed: {
    ...mapGetters(["analyseById"]),
    ...mapState({
      analyses: (state) => state.analyse.analyses,
      apiId: (state) => state.api.apiId,
      apiLoaded: (state) => state.api.apiLoaded,
      documentsPerYear: (state) => state.document.documentsPerYear,
    }),
  },
  watch: {
    apiReady() {
      const yearMin = parseInt(_.minBy(this.documentsPerYear, "year").year);
      const yearMax = parseInt(_.maxBy(this.documentsPerYear, "year").year);
      this.yearRange = [yearMin, yearMax];
      this.yearLimit = [yearMin, yearMax - 2, yearMax];
    },
    yearLimit(val) {
      this.form.startyear = val[0];
      this.form.recentyear = val[1];
      this.form.stopyear = val[2];
    },
  },
  mounted() {
    this.loadFilters();

    if (this.analyses.length > 0) {
      this.extractAnalyseConfig();
    } else {
      this.loadAnalyses().then(() => {
        this.extractAnalyseConfig();
      });
    }

    if (this.documentsPerYear.length > 0) {
      const yearMin = parseInt(_.minBy(this.documentsPerYear, "year").year);
      const yearMax = parseInt(_.maxBy(this.documentsPerYear, "year").year);
      this.yearRange = [yearMin, yearMax];
      this.yearLimit = [
        this.form.startyear,
        this.form.recentyear,
        this.form.stopyear,
      ];
    }
  },
  methods: {
    ...mapActions(["loadAnalyses", "loadFilters", "updateAnalyse"]),
    extractAnalyseConfig() {
      this.analysis = this.analyseById({ id: this.$route.params.id });
      this.form.name = this.analysis.name;
      this.form.description = this.analysis.description;
      this.form.type = this.analysis.config.type;
      this.form.recentyear = this.analysis.config.query.recentyear;
      this.form.startyear = this.analysis.config.query.startyear;
      this.form.stopyear = this.analysis.config.query.stopyear;
      this.form.includeSubset = this.analysis.includeSubset;
      this.form.addClustering = this.analysis.addClustering;

      this.axis.x = this.analysis.axis.x;
      this.axis.y = this.analysis.axis.y;
      this.axis.z = this.analysis.axis.z;

      this.axisReady = true;
    },
    submit() {
      const payload = {
        _id: this.analysis._id,
        name: this.form.name,
        description: this.form.description,
        config: {
          type: this.form.type,
          query: {
            xlayers: AnalysisHelpers.buildQueryLayers(this.axis.x),
            ylayers: [],
            zlayers: [],
            subset_filters: [],
            startyear: this.form.startyear,
            recentyear: this.form.recentyear,
            stopyear: this.form.stopyear,
            postprocessing: [],
          },
        },
        // Add some extra data to easily load when editing analysis
        axis: this.axis,
        includeSubset: this.form.includeSubset,
        addClustering: this.form.addClustering,
      };

      if (
        [
          "heatmap",
          "clustermap"
          //"onedheatmap",
          //"barchart",
          //"columnchart",
          //"linechart"
        ].includes(this.form.type)
      ) {
        payload.config.query.ylayers = AnalysisHelpers.buildQueryLayers(
          this.axis.y
        );

        if (this.form.addClustering) {
          payload.config.query.postprocessing = ["clustering"];
        }
      }

      if (this.form.includeSubset) {
        payload.config.query.zlayers = AnalysisHelpers.buildQueryLayers(
          this.axis.z
        );
      }

      this.allowSubmit = false;
      this.updateAnalyse({
        analyseId: this.analysis._id,
        analyse: payload,
      }).then(() => {
        this.allowSubmit = true;
        this.$toasted.show("Analysis updated");
        setTimeout(() => {
          this.$router.push({
            name: "ExploreAnalysisView",
            params: { id: this.analysis._id },
          });
        }, 1000);
      });
    },
  },
};
</script>
